import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { MsalProvider } from '@azure/msal-react';
import { broadcastResponseToMainFrame } from '@azure/msal-browser/redirect-bridge';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter } from 'react-router-dom';
import './index.css';
import App from './App';
import { msalInstance } from './config/auth';
import { isMsalChildWindow } from './config/authSignals';
import AuthGate from './components/AuthGate';

// Apply saved theme before first render to avoid a flash of the wrong theme.
const savedTheme = localStorage.getItem('theme') || 'light';
document.documentElement.setAttribute('data-theme', savedTheme);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60 * 1000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

async function bootstrap(): Promise<void> {
  // MSAL's hidden iframe — and the Mail.Read popup — land HERE, because the
  // redirect URI is this origin. In msal-browser v5 the window that asked is
  // waiting on a BroadcastChannel for this page to pass the answer back, and it
  // waits in vain if the app renders instead. Worse, the rendered app asks for a
  // token and opens the next hidden iframe: on 2026-10-02 that nested ~20 deep in
  // the Quality app until Entra blocked the client as a loop (AADSTS50196). So:
  // hand the answer back, render nothing, and stop BEFORE MSAL initialises.
  // config/authSignals.ts has the full account.
  if (isMsalChildWindow(window)) {
    try {
      await broadcastResponseToMainFrame();
    } catch (err) {
      // Nobody can see this page. The window that asked times out instead, and
      // for the API token acquireApiToken turns that into a redirect to sign in.
      console.error('[auth] could not hand the sign-in response back to the app', err);
    }
    return;
  }

  await msalInstance.initialize();
  await msalInstance.handleRedirectPromise();

  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <MsalProvider instance={msalInstance}>
        <QueryClientProvider client={queryClient}>
          {/* Inside AuthGate, not outside it: the gate renders its own sign-in
              screen when nobody is authenticated, and that screen has no routes.
              Putting the router here also means the URL somebody arrived on —
              /contracts/VOD-2026-06 from an email — is still intact after the MSAL
              redirect completes, because the gate never navigates. */}
          <AuthGate>
            <BrowserRouter>
              <App />
            </BrowserRouter>
          </AuthGate>
        </QueryClientProvider>
      </MsalProvider>
    </StrictMode>,
  );
}

await bootstrap();
